import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useRequest from "../../../components/hooks/use-request";
import Navbar from "../../../components/layout/navbar";
import Icon from "../../../assets/icons";
import Tab from "../../../components/tab";
import { showToast } from "../../../components/toast";
import { I_Profile } from "../../inventory/types";
import { capitalizeFirstLetter } from "../../../utilz/function";
import { RingLoader } from "react-spinners";
import ActionModal from "./action-modal";
import AddSupplierDialog from "../../../components/suppliers/add-supplier-dialog";
import AddDiscountDialog from "../../../components/discount/editDiscount/add-discount-dialog";

interface DiscountData {
  _id: string;
  discountType: string;
  discountValue: string;
  discountCode: string;
  startDate: any;
  endDate: any;
  termsAndConditions: string;
  eligibleItems: string;
  eligibleCustomers: string;
  minimumPurchaseRequirement: any;
  usageLimit: number;
  name: string;
  email: string;
  contactPerson: string;
  phone: number;
  status: string;
  metrics: any
}

const SupplierDetails = () => {
  const [discount, setDiscount] = useState<DiscountData>();
  const { id } = useParams<{ id: string }>();
  const [supplierModal, setSupplierModal] = useState(false);
  const navigate = useNavigate();
  const [modalConfig, setModalConfig] = useState<{
    isVisible: boolean;
    actionType: "approve" | "delete" | "reject";
  }>({
    isVisible: false,
    actionType: "approve",
  });

  const [profile, setProfile] = useState<I_Profile | null>(null);
  const userToken = localStorage.getItem("token");
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const { makeRequest: getDiscountById } = useRequest(
    `/discounts/${id}`,
    "GET"
  );

  const { makeRequest: deleteSupplier } = useRequest(
    `/discounts/${id}`,
    "DELETE"
  );

  const { makeRequest: approveSupplier, loading } = useRequest(
    `/discounts/${id}/review`,
    "POST",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getDiscountById();
      setDiscount(response?.data?.discount || []);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  const handleConfirmAction = async () => {
    if (
      modalConfig.actionType === "approve" ||
      modalConfig.actionType === "reject"
    ) {
      const [response] = await approveSupplier({
        action: modalConfig.actionType,
      });

      if (response?.status) {
        showToast(response.message, true);
        window.location.reload();
      } else {
        showToast(response?.message, false);
      }
    } else if (modalConfig.actionType === "delete") {
      const [response] = await deleteSupplier();
      if (response?.status) {
        showToast(response.message, true);
        navigate("/discount");
      } else {
        showToast(response?.message, false);
      }
    }
  };

  const handleApprove = () => {
    setModalConfig({
      isVisible: true,
      actionType: "approve",
    });
  };

  const handleDecline = () => {
    setModalConfig({
      isVisible: true,
      actionType: "reject",
    });
  };

  const handleDelete = () => {
    setModalConfig({
      isVisible: true,
      actionType: "delete",
    });
  };

  function closeSupplierModal() {
    setSupplierModal(false);
  }

  function openSupplierModal() {
    setSupplierModal(true);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as I_Profile} />

      <main className="flex-1 container mx-auto py-6">
        <div className="flex justify-between gap-5 mt-5 mx-10">
          <div className="flex gap-4">
            <div
              onClick={() => navigate(-1)}
              className="flex gap-2 font-semibold text-[16px] border-r-2 pr-4 border-separate cursor-pointer"
            >
              <Icon name="appbackIcon" className="mt-1" /> Back
            </div>

            <h1 className="md:text-[30px] -mt-2 font-bold">{discount?.name}</h1>
          </div>

          <div className="text-[14px] flex gap-2">
            {discount?.status !== "pending" && (
              <>
                <button
                  type="button"
                  className="font-normal flex border gap-1 rounded-full px-5 py-1 items-center"
                  onClick={openSupplierModal}
                >
                  <Icon name="editIconBlack" /> Edit
                </button>

                <button
                  type="button"
                  className="font-normal flex bg-[#FFF1F2] text-[#E11D48] border border-[#FDA4AF] gap-1 rounded-full px-5 py-1 items-center"
                  onClick={handleDelete}
                >
                  <Icon name="markIcon" /> Delete
                </button>
              </>
            )}

            {discount?.status === "pending" && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="font-normal flex bg-[#047401] text-[#fff] border gap-1 rounded-full px-5 py-1 items-center"
                  onClick={handleApprove}
                >
                  <Icon name="cancelIcon" /> Approve
                </button>

                <button
                  type="button"
                  className="font-normal flex bg-[#FFF1F2] text-[#E11D48] border border-[#FDA4AF] gap-1 rounded-full px-5 py-1 items-center"
                  onClick={handleDecline}
                >
                  <Icon name="markIcon" /> Decline
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="my-7">
          <div className="flex lg:grid lg:grid-cols-2 gap-4 overflow-x-auto scrollbar-hide lg:overflow-visible whitespace-nowrap">
            <section className="bg-white border border-[#C9C9C9] rounded-3xl p-4 flex flex-col justify-between">
              <p className="font-bold text-[14px] text-[#475467]">
              Total Sales with Discount
                <button type="button"></button>
              </p>
              <h1 className="text-[#2B2B2B] text-[36px] font-extrabold">
                {discount?.metrics?.totalRevenueFromDiscountSales || 0}
              </h1>
            </section>

            {/* Orders Card */}
            <section className="bg-white border border-[#C9C9C9] rounded-3xl p-4 flex flex-col justify-between">
              <p className="font-bold text-[14px] text-[#475467]">
              Total Revenue from Discounted Sales
                <button type="button"></button>
              </p>
              <h1 className="text-[#F59E0B] text-[36px] font-extrabold">
                {discount?.metrics?.totalSalesWithDiscount|| 0}
              </h1>
            </section>

           
          </div>
        </div>

        <div className="flex gap-6 mt-5 mx-10 mb-20">
        <div className="bg-white rounded-3xl shadow border w-full border-[#C9C9C9]">
          <h2 className="text-lg font-semibold mb-4 px-6 pt-3 ">
            Discount Information
          </h2>

          <div className="border-t pt-4 p-6">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-2">
                  <Icon name="queue" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Status</p>
                  <p className="font-medium capitalize bg-[#F5F5F5] rounded-3xl px-5 py-1">
                    {discount?.status}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-2">
                  <Icon name="quotes" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Discount Type</p>
                  <p className="font-medium capitalize">
                    {discount?.discountType}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="tags"/>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Discount Value</p>
                    <p className="font-medium capitalize">
                      {discount?.discountType === "fixed-amount" 
                        ? `₦${discount?.discountValue}` 
                        : `${discount?.discountValue}% off`}
                    </p>
                  </div>
                </div>
              <div className="flex justify-between gap-4 items-center">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="lockKeyOpen" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Discount Code</p>
                    <p className="font-medium capitalize">
                      {discount?.discountCode}
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 self-start">
                  <Icon name="copyIcon" />
                </button>
              </div>

              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-2">
                  <Icon name="calender" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Start Date</p>
                  <p className="font-medium capitalize">
                    {new Date(discount?.startDate).toLocaleDateString("en-GB")}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-2">
                  <Icon name="calender" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">End Date</p>
                  <p className="font-medium capitalize">
                    {new Date(discount?.endDate).toLocaleDateString("en-GB")}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="bg-[#E8F2E8] rounded-full p-2">
                  <Icon name="calender" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">
                    Terms and Conditions
                  </p>
                  <p className="font-medium capitalize">
                    {discount?.termsAndConditions}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow border w-full border-[#C9C9C9]">
          <h2 className="text-lg font-semibold mb-4 px-6 pt-3 ">
            Eligibility and Conditions
          </h2>
          <div className="border-t pt-4 p-6">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between gap-4 items-center">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="cart" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Eligible Items</p>
                    <p className="font-medium capitalize">
                      {discount?.eligibleItems}
                    </p>
                  </div>
                </div>
                {/* <button className="text-gray-400 self-start">
                  <Icon name="copyIcon" />
                </button>   */}
              </div>
              <div className="flex justify-between gap-4 items-center">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="users" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">
                      Eligible Customer
                    </p>
                    <p className="font-medium capitalize">
                      {discount?.eligibleCustomers}
                    </p>
                  </div>
                </div>
                {/* <button className="text-gray-400 self-start">
                  <Icon name="copyIcon" />
                </button>   */}
              </div>
              <div className="flex justify-between gap-4 items-center">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="graph" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">
                      Minimum Purchase
                    </p>
                    <p className="font-medium capitalize">
                      {new Intl.NumberFormat("en-NG", {
                        style: "currency",
                        currency: "NGN",
                      }).format(discount?.minimumPurchaseRequirement || 0)}
                    </p>
                  </div>
                </div>
                {/* <button className="text-gray-400 self-start">
                  <Icon name="copyIcon" />
                </button>   */}
              </div>

              <div className="flex justify-between gap-4 items-center">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-2">
                    <Icon name="database" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Usage Limit</p>
                    <p className="font-medium capitalize">
                      {discount?.usageLimit} use per customer
                    </p>
                  </div>
                </div>
                {/* <button className="text-gray-400 self-start">
                  <Icon name="copyIcon" />
                </button>   */}
              </div>

              <div className="flex gap-4 items-center mt-10">
                <Icon name="infoIcon" />
                <p>This discount isn’t stackable with other discounts</p>
              </div>
            </div>
          </div>
        </div>
        </div>

        {loading ? (
          <RingLoader color="#ffffff" loading={loading} size={20} />
        ) : (
          <ActionModal
            visible={modalConfig.isVisible}
            onCancel={() => setModalConfig({ ...modalConfig, isVisible: false })}
            onConfirm={handleConfirmAction}
            title={
              modalConfig.actionType === "approve"
                ? "Discount Approval"
                : modalConfig.actionType === "reject"
                ? "Discount Decline"
                : "Discount Deletion"
            }
            content={`Are you sure you want to ${modalConfig.actionType} this discount?`}
            actionType={modalConfig.actionType}
          />
        )}

        <AddDiscountDialog
          isVisible={supplierModal}
          onClose={closeSupplierModal}
          discount={discount}
        />

      </main>
     

     


     
    </div>
  );
};

export default SupplierDetails;
